import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  Wallet,
  Calculator,
  PackageCheck,
  Percent,
  HelpCircle,
  Send,
  Check,
  Plus,
  Minus,
  ArrowUpRight,
  ShieldHalf,
  Building2,
  Gift,
  CalendarClock,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { HeroCard } from "@/components/blocks/HeroCard";
import { FaqList } from "@/components/blocks/FaqList";
import { Reveal } from "@/components/Reveal";
import { FAQ_BY_ID } from "@/lib/faq";
import {
  FEATURES,
  CLUB_FEATURES,
  COMPANY_FEATURES,
  isClubOnly,
} from "@/lib/features";
import {
  PLANS,
  COMMISSION_PERCENT,
  FOUNDER_MONTHS,
  FOUNDER_UNTIL,
  EXTRA_SPONSORSHIP_FEE,
  TRIAL_DAYS,
  formatPrice,
  euro,
  pricePair,
  savingOf,
  companyFirstYear,
} from "@/lib/pricing";
import {
  ROUTE_CONTACT,
  ROUTE_DEMO,
  ROUTE_FAQ,
  ROUTE_WAITLIST,
  waitlistFor,
} from "@/lib/brand";
import { STATUS_LAUNCH } from "@/lib/status";

// Bis zum Start als „Geplante Preisstruktur“ gekennzeichnet, siehe Kopf und
// Hinweis über den Abo-Karten.

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "abos", label: "Abos", icon: Wallet },
  { id: "rechner", label: "Rechenbeispiel", icon: Calculator },
  { id: "leistungen", label: "Enthalten", icon: PackageCheck },
  { id: "provision", label: "Provision", icon: Percent },
  { id: "faq", label: "Fragen", icon: HelpCircle },
  { id: "cta", label: "Loslegen", icon: Send },
];

const [CLUB_PLAN, COMPANY_PLAN] = PLANS;

// Die Preisfragen kommen aus src/lib/faq.js, dazu die Frage, ob die
// Warteliste etwas kostet. Alle übrigen stehen auf „Häufige Fragen“.
const PRICE_FAQ = [
  ...FAQ_BY_ID.preise.items,
  ...FAQ_BY_ID.start.items.filter((item) => item.id === "warteliste-kosten"),
];

// Die Texte gehen von einem enthaltenen Sponsoring aus (INCLUDED_SPONSORSHIPS).

const AT_A_GLANCE = [
  {
    icon: ShieldHalf,
    title: "Vereins-Abo",
    text: `${pricePair(CLUB_PLAN.regular)}. Alle Sponsorings ohne Aufschlag.`,
  },
  {
    icon: Building2,
    title: "Unternehmens-Abo",
    text: `${pricePair(COMPANY_PLAN.regular)}. Ein Sponsoring enthalten, jedes weitere einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}.`,
  },
  {
    icon: Gift,
    title: "Gründerpreis",
    text: `${FOUNDER_MONTHS} Monate günstiger für alle, die bis zum ${FOUNDER_UNTIL} abschließen.`,
  },
  {
    icon: Percent,
    title: `${COMMISSION_PERCENT} % Provision`,
    text: "Nur bei Vertragsschluss über MatchGate, nicht für mitgebrachte Sponsorings.",
  },
];

const COMMISSION_POINTS = [
  {
    title: "Nur bei Vertragsschluss",
    text: "Kommt über MatchGate keine Partnerschaft zustande, fällt keine Provision an. Das Abo bleibt davon unberührt.",
  },
  {
    title: "Vorher bekannt",
    text: "Der Betrag ergibt sich direkt aus dem vereinbarten Sponsoringvolumen und steht damit fest, bevor unterschrieben wird.",
  },
  {
    title: "Ein Satz für alle",
    text: "Fünf Prozent gelten unabhängig von Vereinsgröße, Sportart oder Höhe des Sponsorings, ohne Staffeln.",
  },
  {
    title: "Getrennt vom Abo",
    text: "Abo und Provision sind zwei Dinge: Das Abo bezahlt die Plattform, die Provision die erfolgreiche Vermittlung.",
  },
  {
    title: "Mitgebrachte Sponsorings ohne Provision",
    text: `Was ihr schon vor MatchGate vereinbart hattet, bringt ihr ohne Provision mit, auch bei späteren Verlängerungen. Für Unternehmen gilt dabei dasselbe wie bei jedem Sponsoring: Eines ist im Abo enthalten, jedes weitere kostet beim Einrichten einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}.`,
    wide: true,
  },
];

const BTN_PRIMARY =
  "relative inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_30px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_40px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.03] transition-all duration-300";

/** Vereine bekommen zusätzlich Pitch-Generator und Media-Kit-Templates. */
const planFeatures = (plan) =>
  plan.perspective === "club" ? CLUB_FEATURES : COMPANY_FEATURES;

/** Umschalter mit gleitender Pille, z. B. Monatlich/Jährlich. */
const Segmented = ({ options, value, onChange, layoutId, label }) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1"
  >
    {options.map(([optionValue, optionLabel]) => (
      <button
        key={optionLabel}
        type="button"
        onClick={() => onChange(optionValue)}
        aria-pressed={value === optionValue}
        className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
          value === optionValue ? "text-white" : "text-white/50 hover:text-white/80"
        }`}
      >
        {value === optionValue && (
          <motion.span
            layoutId={layoutId}
            className="absolute inset-0 rounded-full bg-white/[0.08] border border-white/15"
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
          />
        )}
        <span className="relative">{optionLabel}</span>
      </button>
    ))}
  </div>
);

/**
 * Gründerpreis groß, regulärer Preis daneben. Bewusst ohne Durchstreichen:
 * gestaffelte Angabe statt Streichpreis.
 */
const PriceBlock = ({ plan, yearly }) => {
  const period = yearly ? "yearly" : "monthly";
  const unit = yearly ? "/ Jahr" : "/ Monat";

  return (
    <div>
      <div className="flex items-baseline gap-2.5">
        <span className="font-display font-black text-5xl text-white">
          {formatPrice(plan.founder[period])}
        </span>
        <span className="text-lg font-semibold text-white/50">€</span>
        <span className="text-sm text-white/40">{unit}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="whitespace-nowrap rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] uppercase tracking-[0.15em] font-bold text-brand-orange">
          Gründerpreis · {FOUNDER_MONTHS} Monate
        </span>
        <span className="text-sm text-white/45">
          regulär {euro(plan.regular[period])} {unit}
        </span>
      </div>

      {yearly && (
        <p className="mt-3 text-xs text-white/40">
          Spart {euro(savingOf(plan.founder))} gegenüber zwölf
          Monatszahlungen, zum regulären Preis{" "}
          {euro(savingOf(plan.regular))}.
        </p>
      )}
    </div>
  );
};

/** Was für Sponsorings gilt: Vereine ohne Aufschlag, Unternehmen je Sponsoring. */
const SponsorshipRules = ({ plan }) => {
  const rows =
    plan.perspective === "company"
      ? [
          { icon: Check, text: "Ein laufendes Sponsoring im Abo enthalten" },
          {
            icon: Plus,
            text: `Jedes weitere einmalig ${euro(EXTRA_SPONSORSHIP_FEE)} beim Einrichten`,
          },
        ]
      : [
          { icon: Check, text: "Alle eure Sponsorings enthalten" },
          { icon: Check, text: "Kein Aufschlag, egal wie viele" },
        ];

  return (
    <ul className="mt-7 space-y-2.5 rounded-2xl border border-white/10 px-5 py-4">
      {rows.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-start gap-3">
          <Icon className="mt-0.5 w-4 h-4 shrink-0 text-brand-orange" strokeWidth={2.5} />
          <span className="text-sm font-semibold leading-relaxed text-white/80">
            {text}
          </span>
        </li>
      ))}
    </ul>
  );
};

const PlanCard = ({ plan, yearly, delay }) => (
  <Reveal delay={delay} className="h-full">
    <div className="group relative h-full flex flex-col overflow-hidden rounded-3xl border border-white/10 p-8 md:p-10 backdrop-blur-xl hover:border-white/20 transition-colors duration-500">
      {/* flex-1 schiebt den Button in beiden Karten auf dieselbe Höhe,
          obwohl die Funktionslisten unterschiedlich lang sind. */}
      <div className="relative flex-1">
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/35">
          {plan.audience}
        </span>
        <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-white">
          {plan.name}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/45 max-w-sm">
          {plan.tagline}
        </p>

        <div className="mt-8">
          <PriceBlock plan={plan} yearly={yearly} />
        </div>

        <SponsorshipRules plan={plan} />

        <div className="mt-8 pt-8 border-t border-white/10">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-white/40 mb-5">
            {planFeatures(plan).length === 1
              ? "Eine Funktion enthalten"
              : `Alle ${planFeatures(plan).length} Funktionen enthalten`}
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {planFeatures(plan).map((f) => (
              <li key={f.id} className="flex items-start gap-3">
                <span className="mt-0.5 flex items-center justify-center w-5 h-5 shrink-0 rounded-full bg-gradient-to-br from-brand-orange to-brand-blue">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
                <span className="text-sm leading-relaxed text-white/65">
                  {f.title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Link
        to={waitlistFor(plan.id)}
        data-testid={`pricing-cta-${plan.id}`}
        className={`${BTN_PRIMARY} mt-9`}
      >
        Platz vormerken
        <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  </Reveal>
);

const MAX_SPONSORSHIPS = 50;

/** Rechenbeispiel für Unternehmen: Abo plus weitere Sponsorings im ersten Jahr. */
const SponsorshipCalculator = () => {
  const [count, setCount] = useState(3);
  const [founder, setFounder] = useState(true);
  const result = companyFirstYear(count, founder);
  const clubYearly = (founder ? CLUB_PLAN.founder : CLUB_PLAN.regular).yearly;

  const stepButton =
    "flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white/80 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-300";

  return (
    <div
      data-testid="pricing-calculator"
      className="relative overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-7 md:p-10"
    >
      <div className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full bg-brand-blue/15 blur-[110px]" />

      <div className="relative">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <span className="block text-xs uppercase tracking-[0.15em] font-bold text-white/45 mb-3">
              Sponsorings im ersten Jahr
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setCount((c) => Math.max(1, c - 1))}
                disabled={count <= 1}
                aria-label="Ein Sponsoring weniger"
                className={stepButton}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span
                aria-live="polite"
                className="min-w-[2.5ch] text-center font-display font-black text-4xl text-white tabular-nums"
              >
                {count}
              </span>
              <button
                type="button"
                onClick={() => setCount((c) => Math.min(MAX_SPONSORSHIPS, c + 1))}
                disabled={count >= MAX_SPONSORSHIPS}
                aria-label="Ein Sponsoring mehr"
                className={stepButton}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Segmented
            label="Preisstufe"
            layoutId="calculator-pill"
            options={[
              [true, "Gründerpreis"],
              [false, "Regulär"],
            ]}
            value={founder}
            onChange={setFounder}
          />
        </div>

        <dl className="mt-8 space-y-3.5 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-white/60">Unternehmens-Abo, jährlich</dt>
            <dd className="shrink-0 whitespace-nowrap font-semibold text-white tabular-nums">
              {euro(result.abo)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-white/60">Ein Sponsoring im Abo enthalten</dt>
            <dd className="shrink-0 whitespace-nowrap font-semibold text-white/50 tabular-nums">0 €</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-white/60">
              {result.extra === 0
                ? "Keine weiteren Sponsorings"
                : `${result.extra} ${
                    result.extra === 1 ? "weiteres Sponsoring" : "weitere Sponsorings"
                  } × ${euro(EXTRA_SPONSORSHIP_FEE)}, einmalig`}
            </dt>
            <dd className="shrink-0 whitespace-nowrap font-semibold text-white tabular-nums">
              {euro(result.extraTotal)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 pt-5 border-t border-white/10">
            <dt className="font-display font-bold text-lg text-white">
              Im ersten Jahr
            </dt>
            <dd className="shrink-0 whitespace-nowrap font-display font-black text-4xl gradient-text tabular-nums">
              {euro(result.total)}
            </dd>
          </div>
        </dl>

        <ul className="mt-8 space-y-2 text-xs leading-relaxed text-white/45">
          <li>
            Endet das enthaltene Sponsoring, wird der Platz wieder frei: Das
            nächste ist dann ohne Aufpreis dabei.
          </li>
          <li>
            Im Jahr danach fällt nur das Abo an, plus {euro(EXTRA_SPONSORSHIP_FEE)}
            für jedes weitere Sponsoring, das neu dazukommt.
          </li>
          <li>
            Über MatchGate vermittelte Sponsorings kosten zusätzlich{" "}
            {COMMISSION_PERCENT} % Provision, mitgebrachte nicht.
          </li>
          <li>
            Zum Vergleich: Ein Verein zahlt unabhängig von der Zahl seiner
            Sponsorings nur sein Abo, {founder ? "zum Gründerpreis" : "regulär"}{" "}
            {euro(clubYearly)} im Jahr.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default function Pricing() {
  const [yearly, setYearly] = useState(false);

  return (
    <PageLayout
      sidebarItems={SIDEBAR_ITEMS}
      title="Preisstruktur"
      testId="pricing-main"
    >
      <PageHero
        fullHeight
        titleFit={11.5}
        eyebrow="Geplante Preisstruktur"
        titleLines={["Unsere", "geplanten Preise,"]}
        titleAccent="offen auf dem Tisch."
        lead={`So sollen die Preise zum Start ${STATUS_LAUNCH.in} aussehen: ein Abo für Vereine, eines für Unternehmen und ${COMMISSION_PERCENT} % Provision nur bei Vertragsschluss. Buchen könnt ihr noch nichts, deshalb zeigen wir offen, was wir planen.`}
        primary={{ label: "Platz vormerken", to: ROUTE_WAITLIST }}
        secondary={{ label: "Zu den Abos", href: "#abos" }}
      >
        <HeroCard kicker="Geplante Preise auf einen Blick" items={AT_A_GLANCE} />
      </PageHero>

      {/* Abos */}
      <section
        id="abos"
        data-testid="pricing-plans-section"
        className="relative py-20 lg:py-28"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-0 left-1/4 w-[600px] h-[420px] rounded-full bg-brand-orange/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10">
            <SectionHeading
              eyebrow="Abos"
              title="Zwei Abos, jeweils "
              accent="voller Funktionsumfang"
              tail="."
              lead={`Keine Funktionsstufen: Was für eure Seite gedacht ist, ist enthalten. Unternehmen zahlen zusätzlich nur für weitere Sponsorings, einmalig beim Einrichten. Wer bis zum ${FOUNDER_UNTIL} abschließt, zahlt ${FOUNDER_MONTHS} Monate lang den Gründerpreis. Testen könnt ihr vorher ${TRIAL_DAYS} Tage kostenlos.`}
              className="max-w-xl mb-0"
            />
            <Reveal delay={0.1}>
              <Segmented
                label="Zahlweise"
                layoutId="billing-pill"
                options={[
                  [false, "Monatlich"],
                  [true, "Jährlich"],
                ]}
                value={yearly}
                onChange={setYearly}
              />
            </Reveal>
          </div>

          <Reveal>
            <div
              data-testid="pricing-planned-note"
              className="mb-6 flex gap-4 rounded-2xl border border-white/10 px-6 py-5"
            >
              <CalendarClock className="w-5 h-5 shrink-0 mt-0.5 text-brand-orange" />
              <p className="text-sm leading-relaxed text-white/60">
                <strong className="font-semibold text-white">
                  Geplant für den Start {STATUS_LAUNCH.in}.
                </strong>{" "}
                Bis dahin kann sich noch etwas ändern. Verbindlich ist, was bei
                eurem Abschluss gilt. Wer auf der{" "}
                <Link
                  to={ROUTE_WAITLIST}
                  className="text-white/80 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300"
                >
                  Warteliste
                </Link>{" "}
                steht, erfährt Änderungen zuerst.
              </p>
            </div>
          </Reveal>

          <div className="grid gap-5 md:gap-6 lg:grid-cols-2 items-stretch">
            {PLANS.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                yearly={yearly}
                delay={i * 0.08}
              />
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-xs leading-relaxed text-white/30">
              Geplante Preise, Änderungen bis zum Start vorbehalten. Alle
              Beträge sind Endpreise. Gemäß § 19 UStG wird keine Umsatzsteuer
              berechnet und daher auch nicht ausgewiesen. Der Gründerpreis gilt
              für Abschlüsse bis zum {FOUNDER_UNTIL} jeweils {FOUNDER_MONTHS}{" "}
              Monate ab Abschluss, danach gilt der reguläre Preis. Die einmalige
              Gebühr für weitere Sponsorings ({euro(EXTRA_SPONSORSHIP_FEE)}) gilt
              auch zum Gründerpreis und fällt bei Verlängerung mit demselben
              Partner nicht erneut an. Nach dem Start lässt sich MatchGate{" "}
              {TRIAL_DAYS} Tage kostenlos und ohne Zahlungsdaten testen.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Rechenbeispiel */}
      <section
        id="rechner"
        data-testid="pricing-calculator-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Rechenbeispiel"
                title="Was ein Unternehmen "
                accent="im ersten Jahr"
                tail=" zahlt."
                lead={`Ein Sponsoring ist im Abo enthalten, jedes weitere kostet beim Einrichten einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}. So zahlt ein Unternehmen mit einem Sponsoring weniger als eines mit zehn. Für Vereine gibt es keinen Aufschlag.`}
                className="max-w-md mb-0"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.05}>
              <SponsorshipCalculator />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Enthalten */}
      <section
        id="leistungen"
        data-testid="pricing-features-section"
        className="relative py-24 lg:py-32 border-y border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Enthalten"
            title="Was in den Abos "
            accent="drinsteckt"
            tail="."
            lead="Sechs Funktionen nutzen beide Seiten. Pitch-Generator und Media-Kit-Templates sind Werkzeuge für die Vereinsarbeit und deshalb nur im Vereins-Abo."
          />

          <div className="grid gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const clubOnly = isClubOnly(f);
              return (
                <Reveal key={f.id} delay={i * 0.04}>
                  <div className="h-full rounded-2xl border border-white/10 backdrop-blur-xl p-6 hover:border-white/20 transition-colors duration-500">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10">
                        <Icon className="w-[18px] h-[18px] text-brand-orange" />
                      </span>
                      <span
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold ${
                          clubOnly
                            ? "border-white/15 text-brand-orange"
                            : "border-white/10 text-white/35"
                        }`}
                      >
                        {clubOnly ? "Nur Verein" : "Beide"}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-white mb-1.5">
                      {f.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/55">
                      {f.short}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Provision */}
      <section
        id="provision"
        data-testid="pricing-commission-section"
        className="relative py-24 lg:py-32"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[420px] rounded-full bg-brand-blue/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Provision"
            title="Fünf Prozent, aber erst, wenn ihr "
            accent="zusammenfindet"
            tail="."
            lead="Neben dem Abo finanziert sich MatchGate über eine Vermittlungsprovision bei Vertragsschluss."
          />

          <div className="grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
                <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-orange/20 blur-[90px]" />
                <div className="relative">
                  <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.06] border border-white/10 mb-7">
                    <Percent className="w-5 h-5 text-brand-orange" />
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-black text-6xl gradient-text">
                      {COMMISSION_PERCENT}
                    </span>
                    <span className="font-display font-bold text-2xl text-white/60">
                      %
                    </span>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-white/60">
                    Provision bei Vertragsschluss über MatchGate. Ohne Abschluss
                    fällt sie nicht an.
                  </p>
                </div>
              </div>
            </Reveal>

            <div className="lg:col-span-7 grid gap-5 sm:grid-cols-2">
              {COMMISSION_POINTS.map((c, i) => (
                <Reveal
                  key={c.title}
                  delay={0.05 + i * 0.05}
                  className={c.wide ? "sm:col-span-2" : ""}
                >
                  <div className="h-full rounded-3xl border border-white/10 backdrop-blur-xl p-7 hover:border-white/20 transition-colors duration-500">
                    <h3 className="font-display font-bold text-xl text-white mb-2.5">
                      {c.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/55">
                      {c.text}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid="pricing-faq-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Fragen zum Preis"
                title="Was ihr zu Abo und Provision "
                accent="wissen solltet"
                tail="."
                lead="Fragen zur Plattform und zur Auswertung findet ihr gesammelt auf der Seite mit den häufigen Fragen."
                className="max-w-sm mb-8"
              />
              <Reveal delay={0.1}>
                <Link
                  to={ROUTE_FAQ}
                  className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white/85 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300"
                >
                  Alle häufigen Fragen
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </Link>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-8">
            <FaqList items={PRICE_FAQ} />
          </div>
        </div>
      </section>

      <CTABand
        title="Unsicher, ob sich das für euch "
        accent="rechnet"
        tail="?"
        lead="Sagt uns, wie groß euer Verein ist oder was ihr als Unternehmen vorhabt. Wir rechnen es ehrlich mit euch durch."
        primary={{ label: "Kontakt aufnehmen", to: ROUTE_CONTACT }}
        secondary={{ label: "Demo ansehen", to: ROUTE_DEMO }}
      />
    </PageLayout>
  );
}
